import React, {useEffect, useState, useRef} from "react";
import {Button, Card, Col, Form, Input, Row, Select, Switch, Tooltip} from "antd";
import {PlusOutlined, DeleteOutlined} from "@ant-design/icons";
import {useFormik} from "formik";
import {poolFormValidation} from "./poolFormValidation";
import {ErrorFocus} from "../../../_defaultComponents/ErrorFocus/ErrorFocus";
import {useTranslation} from "react-i18next";
import {CityFilter, PoolIntegrationTypeFilter} from "../../../_defaultComponents/FilterComponents";
import {useStore} from "../../../../hooks/useStore";
import {useHistory, useLocation} from "react-router-dom";
import {PoolFormCompanyTable} from "../poolFormCompanyTable/PoolFormCompanyTable";
import {H3Map} from "../../../H3Map/H3Map";
import {useFetch} from "../../../../hooks/useFetch";

const defaultValues = {
    name: "",
    isActive: false,
    city: "",
    companyInfoArray: [],
    isTestPool: false
};
const genereteInitialValues = pool => {
    if (!pool) return defaultValues;
    const initialValues = {
        name: pool.name,
        isActive: pool.isActive,
        city: pool.city,
        companies: pool.companies.map(company => company._id.toString()),
        subCompanies: pool.subCompanies.map(subCompany => subCompany._id.toString()),
        companyInfoArray: pool.companies.map(company => ({
            company: company._id.toString(),
            subCompanies: pool.subCompanies
                .filter(subCompany => subCompany.companyId.toString() === company._id.toString())
                .map(subCompany => subCompany._id.toString())
        })),
        isTestPool: pool.isTestPool
    };
    return initialValues;
};
export const PoolForm = ({
    pool = undefined,
    subCompanyOptions = [],
    subCompanyOptionsLoading,
    type = "create", //or edit
    loadSubCompanies = () => {},
    onSelectedSubCompaniesChange = () => {},
    subCompanySearchText = "",
    setSubCompanySearchText = () => {}
}) => {
    const {t} = useTranslation(["pools"]);
    const stores = useStore();
    const history = useHistory();
    const location = useLocation();
    const [h3Cells, setH3Cells] = useState(pool?.h3Cells || []);
    const mapRef = useRef(null);
    const [integrationType, setIntegrationType] = useState(pool?.integrationType || "pool");

    const getInitialCity = () => {
        if (type === "edit" && pool?.city) return pool.city;
        const searchParams = new URLSearchParams(location.search);
        const cityParam = searchParams.get("city");
        return cityParam || "";
    };

    useEffect(() => {
        const initialCity = getInitialCity();
        if (initialCity) {
            setTimeout(() => {
                if (mapRef.current) {
                    mapRef.current.panToCity(initialCity);
                }
            }, 1000);
        }
    }, [location.search, type, pool?.city]);

    const formik = useFormik({
        initialValues: {
            ...(type === "edit" ? genereteInitialValues(pool) : defaultValues),
            city: getInitialCity()
        },
        validationSchema: poolFormValidation,
        enableReinitialize: true,
        validateOnBlur: false,
        validateOnChange: true,

        onSubmit: async values => {
            const companies = new Set();
            const subCompanies = new Set();
            values.companyInfoArray
                .filter(companyInfo => companyInfo.subCompanies.length > 0)
                .forEach(companyInfo => {
                    companies.add(companyInfo.company);
                    companyInfo.subCompanies.forEach(subCompany => subCompanies.add(subCompany));
                });
            const payload = {
                name: values.name,
                city: values.city,
                isActive: values.isActive,
                companies: [...companies],
                subCompanies: [...subCompanies],
                couriers: type === "edit" ? pool?.couriers?.map(c => c._id.toString()) || [] : [],
                integrationType: integrationType,
                isTestPool: values.isTestPool,
                h3Cells: h3Cells
            };

            try {
                const result = {hasError: true, poolId: undefined};
                if (type === "edit") {
                    const updateResult = await stores.poolStore.updatePool({
                        ...payload,
                        poolId: pool?._id?.toString()
                    });
                    result.hasError = !Boolean(updateResult?.failed?.length === 0);
                    result.poolId = updateResult.result?._id?.toString();
                } else if (type === "create") {
                    const createResult = await stores.poolStore.createPool(payload);
                    result.hasError = !Boolean(createResult?.success);
                    result.poolId = createResult.data?._id?.toString();
                }

                if (result.hasError) return;

                history.push(`/pools/${result.poolId}`);
            } catch (error) {}
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues
    } = formik;

    const [isCityH3CellsLoading, cityH3Cells] = useFetch({
        fetchFn: stores.poolStore.getPoolH3Cells.bind(null, {city: values.city}),
        dependencies: [values.city],
        initialCall: values.city !== ""
    });

    const otherHexagons = React.useMemo(() => {
        if (!cityH3Cells?.data || !Array.isArray(cityH3Cells.data)) {
            return [];
        }

        const hexagons = cityH3Cells.data.flatMap(pool => pool.h3Cells || []);

        return hexagons;
    }, [cityH3Cells]);

    useEffect(() => {
        refreshSelectedSubCompanies();
    }, [values.companyInfoArray, subCompanyOptions]);

    const refreshSelectedSubCompanies = () => {
        const currentSelectedSubCompanies = new Set();
        values.companyInfoArray.forEach(companyInfo => {
            companyInfo.subCompanies.forEach(subCompany =>
                currentSelectedSubCompanies.add(subCompany)
            );
        });
        onSelectedSubCompaniesChange([...currentSelectedSubCompanies]);
    };

    const handleCityChange = city => {
        if (mapRef.current && city) {
            mapRef.current.panToCity(city);
        }
        setValues({
            ...values,
            city,
            companyInfoArray: [
                ...values.companyInfoArray.map(companyInfo => ({...companyInfo, subCompanies: []}))
            ],
            companies: [],
            subCompanies: []
        });
    };

    const handleCompanyChange = ({company, index}) => {
        setFieldValue(`companyInfoArray[${index}]`, {company, subCompanies: []});
        loadSubCompanies({companyIds: [company]});
    };

    const handleSubcompaniesChange = ({subCompanies, index}) => {
        setFieldValue(`companyInfoArray[${index}].subCompanies`, subCompanies);
    };

    const AddCompanyButton = () => {
        return (
            <Tooltip title={t("tooltips.addCompany", {ns: "pools"})}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    loading={isSubmitting}
                    onClick={() => {
                        setFieldValue("companyInfoArray", [
                            ...values.companyInfoArray,
                            {company: undefined, subCompanies: []}
                        ]);
                    }}
                >
                    {t("actions.addCompany", {ns: "pools"})}
                </Button>
            </Tooltip>
        );
    };
    const DeleteCompanyButton = ({index}) => {
        return (
            <Tooltip title={t("tooltips.removeCompany", {ns: "pools"})}>
                <Button
                    type="danger"
                    style={{marginTop: "4px"}}
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        setFieldValue(
                            "companyInfoArray",
                            values.companyInfoArray.filter((_, i) => i !== index)
                        );
                    }}
                />
            </Tooltip>
        );
    };

    return (
        <Form
            autoComplete="off"
            data-testid="newUserForm"
            labelCol={{span: 24}}
            wrapperCol={{span: 24}}
            onFinish={handleSubmit}
        >
            <ErrorFocus formik={formik} />
            <Row gutter={[0, 20]} style={{flexDirection: "column"}}>
                <Col>
                    <Form.Item
                        label={t("fields.poolRegionName", {ns: "pools"})}
                        validateStatus={errors.name && touched.name ? "error" : "success"}
                        help={errors.name && touched.name ? errors.name : null}
                    >
                        <Input
                            name="name"
                            data-testid="poolName"
                            value={values.name}
                            onChange={handleChange}
                        />
                    </Form.Item>
                </Col>
                <Col>
                    <Form.Item
                        label={t("fields.city", {ns: "common"})}
                        validateStatus={errors.city && touched.city ? "error" : "success"}
                        help={errors.city && touched.city ? errors.city : null}
                    >
                        {type === "create" ? (
                            <CityFilter
                                name="city"
                                data-testid="poolCity"
                                value={values.city}
                                onChange={handleCityChange}
                                showAllOption={false}
                                showLabel={false}
                                selectMarginBottom="0px"
                            />
                        ) : (
                            <span style={{border: "1px solid #DEDEDE", padding: "10px 15px"}}>
                                {values.city.charAt(0).toUpperCase() + values.city.slice(1)}
                            </span>
                        )}
                    </Form.Item>
                </Col>
                <Col>
                    <Form.Item label={t("filters.integrationType", {ns: "common"})}>
                        <PoolIntegrationTypeFilter
                            value={integrationType}
                            onChange={value => setIntegrationType(value)}
                        />
                    </Form.Item>
                </Col>
                <Row gutter={[0, 10]}>
                    <Col>
                        <label>{t("fields.companies", {ns: "pools"})}</label>
                    </Col>
                    <Col span="24">
                        <PoolFormCompanyTable
                            pool={pool}
                            formValues={values}
                            setFormValues={setValues}
                            subCompanyOptions={subCompanyOptions}
                        />
                    </Col>
                </Row>

                <Row gutter={[16, 0]} align="middle" style={{marginBottom: 16}}>
                    <Col style={{minWidth: 160, textAlign: "center"}}>
                        <div style={{marginBottom: 8}}>{t("fields.status", {ns: "common"})}</div>
                        <Switch
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            checked={values.isActive}
                            onChange={value => setFieldValue("isActive", Boolean(value))}
                        />
                    </Col>
                    <Col style={{minWidth: 160, textAlign: "center"}}>
                        <div style={{marginBottom: 8}}>{t("fields.testPool", {ns: "common"})}</div>
                        <Switch
                            checked={values.isTestPool}
                            onChange={value => setFieldValue("isTestPool", Boolean(value))}
                        />
                    </Col>
                </Row>
                <Row style={{justifyContent: "flex-end"}}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        {type === "edit"
                            ? t("actions.update", {ns: "common"})
                            : t("actions.save", {ns: "common"})}
                    </Button>
                </Row>
                <Row style={{marginTop: "20px"}}>
                    <H3Map
                        ref={mapRef}
                        markers={values?.subCompanies?.map(subCompany => ({
                            _id: subCompany._id,
                            name: subCompany.name,
                            location: subCompany.location
                        }))}
                        initialHexagons={h3Cells}
                        otherHexagons={otherHexagons}
                        onHexagonSelect={hexagons => {
                            setH3Cells(hexagons);
                        }}
                    />
                </Row>
            </Row>
        </Form>
    );
};
